import { motion, useTransform, useViewportScroll } from 'framer-motion';
import React, { useEffect, useState, useRef } from 'react';

import globe from '../../../main/assets/images/orqaDeveloperProgram/globe.png';
import globeMobile from '../../../main/assets/images/orqaDeveloperProgram/globe-mobile.png';

const DOT_POSITIONS = [
  { left: '50%', top: '-20px', size: 4 },
  { left: '40%', top: '-60px', size: 2 },
  { left: '60%', top: '-45px', size: 3 },
  { left: '35%', top: '-30px', size: 3 },
  { left: '75%', top: '-80px', size: 2 },
  { left: '15%', top: '-70px', size: 2 },
  { left: '70%', top: '-25px', size: 4 },
  { left: '47%', top: '-90px', size: 2 },
  { left: '55%', top: '-15px', size: 5 },
  { left: '33%', top: '-10px', size: 5 },
];

const DOT_POSITIONS_MOBILE = [
  { left: '50%', top: '-20px', size: 4 },
  { left: '60%', top: '-45px', size: 3 },
  { left: '35%', top: '-30px', size: 3 },
  { left: '75%', top: '-80px', size: 2 },
  { left: '70%', top: '45px', size: 4 },
  { left: '47%', top: '-90px', size: 2 },
  { left: '33%', top: '55px', size: 5 },
];

export const Hero = () => {
  const [animEnd, setAnimEnd] = useState(1000);
  const [isDesktop, setIsDesktop] = useState(true);
  const [isSmallImage, setIsSmallImage] = useState(true);

  const targetSectionRef = useRef<HTMLDivElement>(null);
  const mobileTextRef = useRef<HTMLParagraphElement>(null);

  const { scrollY } = useViewportScroll();

  // -- Glow Animations --
  const dynamicStrokeWidth = useTransform(scrollY, [0, animEnd], [15, 27]);
  const rawBlurValue = useTransform(scrollY, [0, animEnd], [18, 42]);
  const dynamicBlur = useTransform(rawBlurValue, val => `blur(${val}px)`);
  const dynamicOffset = useTransform(scrollY, [0, animEnd], [0.09, 0.81]);

  // -- Globe Animations --
  const globeScale = useTransform(scrollY, [0, animEnd], [1.8, 0.75]);
  const globeGroupY = useTransform(scrollY, [0, animEnd], ['-15vh', '32vh']);

  // -- Opacity Animations --
  const textOpacity = useTransform(scrollY, [animEnd * 0.75, animEnd], [0, 1]);
  const voidOpacity = useTransform(scrollY, [0, animEnd], [1, 0]);
  const dotsOpacity = useTransform(scrollY, [0, animEnd * 0.2], [1, 0]);

  const handleExploreClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (isDesktop && targetSectionRef.current) {
      const elementRect = targetSectionRef.current.getBoundingClientRect();
      const dynamicOffset = window.innerHeight * 0.3;

      window.scrollTo({
        top: elementRect.top + window.scrollY + dynamicOffset,
        behavior: 'smooth',
      });
    } else if (!isDesktop && mobileTextRef.current) {
      mobileTextRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1200);
      setIsSmallImage(window.innerWidth > 550);

      let calculatedEnd = window.innerHeight;

      if (calculatedEnd > 1000) calculatedEnd = 1000;

      const maxScrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      if (maxScrollable > 0 && calculatedEnd > maxScrollable) {
        calculatedEnd = maxScrollable;
      }

      setAnimEnd(calculatedEnd);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section className="program-hero-section">
      {isDesktop && (
        <motion.div
          className="program-hero-section-bottom-overlay"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100%',
            background:
              'linear-gradient(to top, rgb(0, 0, 0) 0%, rgb(0, 0, 0) 30%, rgba(0, 0, 0, 0) 100%)',
            pointerEvents: 'none',
            zIndex: 50,
            opacity: voidOpacity,
          }}
        />
      )}

      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="1014"
        height="1218"
        viewBox="0 0 1014 1218"
        fill="none"
        style={{
          position: 'absolute',
          top: -300,
          left: 0,
          right: 0,
          marginInline: 'auto',
        }}
      >
        <rect
          width="1013.43"
          height="1217.27"
          rx="506.716"
          fill="url(#paint0_radial_6545_4796)"
        />
        <defs>
          <radialGradient
            id="paint0_radial_6545_4796"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(506.716 608.637) rotate(90) scale(608.637 506.716)"
          >
            <stop stopColor="#FFBFC6" stopOpacity="0.26" />
            <stop offset="1" stopColor="#FF2238" stopOpacity="0" />
          </radialGradient>
        </defs>
      </svg>

      <div className="program-hero-content">
        <div className="program-hero-titles">
          <h2 className="program-hero-subtitle">
            Ready to shape the future of robotics?
          </h2>
          <h1 className="program-hero-title">Orqa Developer Program</h1>
        </div>

        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="824"
          height="57"
          viewBox="0 0 824 57"
          fill="none"
        >
          <g filter="url(#filter0_d_6495_4790)">
            <path
              d="M27.7 28.2031H795.7"
              stroke="url(#paint0_linear_6495_4790)"
              shapeRendering="crispEdges"
            />
          </g>
          <defs>
            <filter
              id="filter0_d_6495_4790"
              x="-3.8147e-06"
              y="0.00312424"
              width="823.4"
              height="56.4"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset />
              <feGaussianBlur stdDeviation="13.85" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0.87451 0 0 0 0 0.113725 0 0 0 0 0.192157 0 0 0 1 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_6495_4790"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_6495_4790"
                result="shape"
              />
            </filter>
            <linearGradient
              id="paint0_linear_6495_4790"
              x1="27.7"
              y1="28.7031"
              x2="795.7"
              y2="28.7031"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#EE2737" stopOpacity="0" />
              <stop offset="0.3" stopColor="#EE2737" />
              <stop offset="0.7" stopColor="#EE2737" />
              <stop offset="1" stopColor="#EE2737" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        <div className="program-hero-actions">
          <div className="program-hero-description">
            The Orqa Developer Program is an exclusive B2B ecosystem that
            provides developers and companies with the tools, resources, and
            support they need to build tomorrow's drone applications today. At
            its heart is a unique synergy of Orqa hardware and software that
            empowers program participants to create innovative solutions using
            our proven infrastructure.
          </div>
          <div className="dreamX__discover--button">
            <a
              href="#explore-target"
              className="btn btn--primary btn--med"
              style={{ zIndex: '99' }}
              onClick={handleExploreClick}
              onMouseDown={e => e.preventDefault()}
            >
              explore
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                style={{ marginLeft: '16px' }}
              >
                <g clipPath="url(#clip0_6495_4795)">
                  <path
                    d="M15.3516 8.60156L8 15.9531L0.648438 8.60156L1.35156 7.89844L7.5 14.0469V0H8.5V14.0469L14.6484 7.89844L15.3516 8.60156Z"
                    fill="currentColor"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_6495_4795">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </a>
          </div>
        </div>
      </div>

      <div
        ref={targetSectionRef}
        id="explore-target"
        style={{
          position: 'relative',
          marginTop: isDesktop ? '200px' : '60px',
          width: '100%',
          flex: 1,
        }}
      >
        {isDesktop ? (
          <motion.div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              y: globeGroupY,
              willChange: 'transform',
            }}
          >
            {/* Desktop Globe Content (unchanged) */}
            <motion.div
              style={{
                width: '100%',
                maxWidth: '1200px',
                scale: globeScale,
                transformOrigin: 'top center',
                pointerEvents: 'none',
                zIndex: 1,
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                willChange: 'transform',
              }}
            >
              <motion.div
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  opacity: dotsOpacity,
                }}
              >
                <svg width="0" height="0" style={{ position: 'absolute' }}>
                  <defs>
                    <linearGradient
                      id="dot_gradient"
                      x1="1.5"
                      y1="0"
                      x2="1.5"
                      y2="3"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="white" stopOpacity="0.04" />
                      <stop offset="1" stopColor="white" stopOpacity="0.12" />
                    </linearGradient>
                  </defs>
                </svg>
                {DOT_POSITIONS.map((pos, index) => (
                  <svg
                    key={index}
                    xmlns="http://www.w3.org/2000/svg"
                    width={pos.size}
                    height={pos.size}
                    viewBox="0 0 3 3"
                    className="program-globe-dots"
                    style={{
                      position: 'absolute',
                      left: pos.left,
                      top: pos.top,
                      mixBlendMode: 'hard-light',
                    }}
                  >
                    <circle
                      cx="1.5"
                      cy="1.5"
                      r="1.5"
                      fill="url(#dot_gradient)"
                    />
                  </svg>
                ))}
              </motion.div>
              <div style={{ position: 'relative', width: '100%' }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="100%"
                  height="auto"
                  viewBox="0 0 2022 2022"
                  fill="none"
                  overflow="visible"
                >
                  <circle
                    cx="1010.53"
                    cy="1010.53"
                    r="952.029"
                    stroke="url(#paint0_linear_base)"
                    strokeWidth="3"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_base"
                      x1="1010.53"
                      y1="58"
                      x2="1010.53"
                      y2="1963.06"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#FD8690" />
                      <stop offset="0.5" stopColor="#FE4352" />
                      <stop offset="1" stopColor="#4A0006" />
                    </linearGradient>
                  </defs>
                </svg>

                <motion.div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    filter: dynamicBlur,
                    WebkitFilter: dynamicBlur,
                    transform: 'translateZ(0)',
                    pointerEvents: 'none',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="100%"
                    height="auto"
                    viewBox="0 0 2022 2022"
                    fill="none"
                    overflow="visible"
                  >
                    <motion.circle
                      cx="1010.53"
                      cy="1010.53"
                      r="952.529"
                      stroke="url(#paint1_linear_glow)"
                      strokeWidth={dynamicStrokeWidth}
                    />
                    <defs>
                      <linearGradient
                        id="paint1_linear_glow"
                        x1="1010.53"
                        y1="58"
                        x2="1010.53"
                        y2="1963.06"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#FD8690" />
                        <motion.stop
                          stopColor="#FF0015"
                          offset={dynamicOffset}
                        />
                        <stop offset="1" stopColor="#4A0006" />
                      </linearGradient>
                    </defs>
                  </svg>
                </motion.div>
              </div>
            </motion.div>

            <motion.div
              className="program-hero-globe-container"
              style={{
                position: 'absolute',
                top: '35%',
                transform: 'translateY(-35%)',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '20px',
                opacity: textOpacity,
              }}
            >
              <p className="program-hero-globe-text">
                Built on Orqa’s high-performance operating environment, our
                Developer Tool Kit (DTK) is a rock-solid modular hardware
                foundation for developers who want to scale state-of-the-art
                robotic solutions with confidence.
              </p>
            </motion.div>
          </motion.div>
        ) : (
          <div className="program-hero-mobile-target">
            <div style={{ position: 'relative', width: '100%' }}>
              <img
                src={!isSmallImage ? globe : globeMobile}
                alt="Orqa Developer Tool Kit Globe"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />

              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                }}
              >
                <svg width="0" height="0" style={{ position: 'absolute' }}>
                  <defs>
                    <linearGradient
                      id="dot_gradient_mobile"
                      x1="1.5"
                      y1="0"
                      x2="1.5"
                      y2="3"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="white" stopOpacity="0.04" />
                      <stop offset="1" stopColor="white" stopOpacity="0.12" />
                    </linearGradient>
                  </defs>
                </svg>

                {DOT_POSITIONS_MOBILE.map((pos, index) => (
                  <svg
                    key={`mobile-dot-${index}`}
                    xmlns="http://www.w3.org/2000/svg"
                    width={pos.size}
                    height={pos.size}
                    viewBox="0 0 3 3"
                    className="program-globe-dots"
                    style={{
                      position: 'absolute',
                      left: pos.left,
                      top: pos.top,
                      mixBlendMode: 'hard-light',
                    }}
                  >
                    <circle
                      cx="1.5"
                      cy="1.5"
                      r="1.5"
                      fill="url(#dot_gradient_mobile)"
                    />
                  </svg>
                ))}
              </div>
            </div>

            <div className="program-hero-globe-container">
              <p className="program-hero-globe-text" ref={mobileTextRef}>
                Built on high-performance infrastructure, the Developer Tool Kit
                (DTK) offers a modular hardware foundation that empowers
                developers to scale their robotic solutions with confidence
              </p>
            </div>
          </div>
        )}
      </div>

      <svg
        className="program-hero-glow-svg"
        xmlns="http://www.w3.org/2000/svg"
        width="1848"
        height="2130"
        viewBox="0 0 1848 2130"
        fill="none"
      >
        <rect
          x="-281.737"
          width="2129.74"
          height="2129.73"
          rx="1064.87"
          fill="url(#paint0_radial_6495_4783)"
        />
        <defs>
          <radialGradient
            id="paint0_radial_6495_4783"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(783.132 1064.87) rotate(90) scale(1064.87 1064.87)"
          >
            <stop stopColor="#FF2238" stopOpacity="0.16" />
            <stop offset="1" stopColor="#FF2238" stopOpacity="0" />
          </radialGradient>
        </defs>
      </svg>
    </section>
  );
};
